import messaging, {
  FirebaseMessagingTypes,
} from '@react-native-firebase/messaging';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';
import { getAuthToken } from './auth/tokenStorage';

import { createLogger } from '../utils/logger';

const logger = createLogger('PushNotification');

class PushNotificationService {
  /**
   * Request push notification permission from the user.
   * On Android 13+, this triggers the POST_NOTIFICATIONS permission dialog.
   */
  async requestPermission(): Promise<boolean> {
    try {
      const authStatus = await messaging().requestPermission();
      const enabled =
        authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
        authStatus === messaging.AuthorizationStatus.PROVISIONAL;

      logger.info('Permission request result:', authStatus);
      logger.info('Notifications enabled:', enabled);

      return enabled;
    } catch (error) {
      logger.error('Failed to request notification permission:', error);
      return false;
    }
  }

  /**
   * Get the FCM token and register it with the backend.
   * Stores the token in AsyncStorage for change detection.
   */
  async getAndRegisterToken(): Promise<string | null> {
    try {
      const fcmToken = await messaging().getToken();
      logger.info('FCM token retrieved:', fcmToken ? `${fcmToken.substring(0, 20)}...` : 'null');

      if (!fcmToken) {
        logger.warn('No FCM token available');
        return null;
      }

      const authToken = await getAuthToken();
      if (!authToken) {
        logger.warn('No auth token available - cannot register device token');
        return fcmToken;
      }

      const response = await fetch(`${API_URL}/notifications/device-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          token: fcmToken,
          platform: Platform.OS,
          deviceId: Platform.OS,
        }),
      });

      if (response.ok) {
        logger.info('Device token registered successfully');
      } else {
        logger.error('Failed to register device token:', response.status);
      }

      await AsyncStorage.setItem('fcmToken', fcmToken);

      return fcmToken;
    } catch (error) {
      logger.error('Failed to get and register FCM token:', error);
      return null;
    }
  }

  /**
   * Listen for FCM token refreshes and re-register with the backend.
   * Returns an unsubscribe function.
   */
  setupTokenRefreshListener(): () => void {
    const unsubscribe = messaging().onTokenRefresh((newToken: string) => {
      logger.info('FCM token refreshed:', newToken ? `${newToken.substring(0, 20)}...` : 'null');
      this.getAndRegisterToken();
    });

    return unsubscribe;
  }

  /**
   * Handle foreground push notifications.
   * Calls the optional callback to refresh unread count, etc.
   * Returns an unsubscribe function.
   */
  setupForegroundHandler(onNotificationReceived?: () => void): () => void {
    const unsubscribe = messaging().onMessage(
      async (remoteMessage: FirebaseMessagingTypes.RemoteMessage) => {
        logger.info('Foreground notification received:', remoteMessage.notification?.title);
        logger.data('Foreground message data', remoteMessage.data);

        if (onNotificationReceived) {
          onNotificationReceived();
        }
      },
    );

    return unsubscribe;
  }

  /**
   * Handle a notification being opened by the user.
   * Extracts action data for deep linking (navigation to be integrated later).
   */
  async handleNotificationOpen(remoteMessage: any): Promise<void> {
    if (!remoteMessage?.data) {
      logger.info('Notification opened with no data');
      return;
    }

    const { actionType, actionData, screen } = remoteMessage.data;

    logger.info('Notification opened:', {
      actionType,
      actionData,
      screen,
    });

    // Deep link navigation will be added when we integrate with navigation
  }

  /**
   * Remove the FCM token from the backend and device.
   * Should be called on logout.
   */
  async removeToken(): Promise<void> {
    try {
      const fcmToken = await AsyncStorage.getItem('fcmToken');

      if (fcmToken) {
        const authToken = await getAuthToken();

        if (authToken) {
          const response = await fetch(`${API_URL}/notifications/device-token`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ token: fcmToken }),
          });

          if (response.ok) {
            logger.info('Device token removed from backend');
          } else {
            logger.error('Failed to remove device token from backend:', response.status);
          }
        }
      }

      await messaging().deleteToken();
      await AsyncStorage.removeItem('fcmToken');

      logger.info('FCM token deleted and removed from storage');
    } catch (error) {
      logger.error('Failed to remove FCM token:', error);
    }
  }
}

export const pushNotificationService = new PushNotificationService();
export default PushNotificationService;
